import { ChevronLeftOutlineIcon, ChevronRightOutlineIcon } from '@ikyucom/design-system-icons-react'
import type { ComponentType, PropsWithChildren } from 'react'
import { css, cva } from '~/styled-system/css'
import { hstack, square } from '~/styled-system/patterns'

type Props = {
  // 1ページあたりの表示数
  pageSize: number
  // 現在のページ番号
  current: number
  // 表示するデータの総数
  total: number
  // 現在のページの左右に表示するページ番号の数
  around: number
  // prev, next を表示するか
  showPrevNext?: boolean
  // 最後のページ番号を表示するか (パフォーマンス的に存在するのは好ましくない)
  showLast?: boolean
  // イベントがトリガされるページボタンを囲むコンポーネント
  PageButtonTrigger: ComponentType<PropsWithChildren<{ page: number }>>
}

/** ページャー */
export function Pager(props: Props) {
  const items = pagerItems(props)

  return (
    <div className={hstack({ gap: '2', color: 'tertiary', fontSize: 'md', fontWeight: 'bold' })}>
      {items.map((item) => {
        const key = `${item.type}-${'page' in item ? item.page : ''}`
        return <PagerItem {...item} key={key} PageButtonTrigger={props.PageButtonTrigger} />
      })}
    </div>
  )
}

function PagerItem(props: PagerItemProps & Pick<Props, 'PageButtonTrigger'>) {
  if (props.type === 'ellipsis') {
    return <div className={css({ fontWeight: 'normal', color: 'gray.800' })}>...</div>
  }
  if (props.type === 'current') {
    return <PagerButton type={props.type}>{props.page}</PagerButton>
  }
  if (props.type === 'prev' || props.type === 'next') {
    const button = (
      <PagerButton type={props.type} disabled={props.page === undefined}>
        {props.type === 'prev' ? <ChevronLeftOutlineIcon /> : <ChevronRightOutlineIcon />}
      </PagerButton>
    )
    return props.page === undefined ? (
      button
    ) : (
      <props.PageButtonTrigger page={props.page}>{button}</props.PageButtonTrigger>
    )
  }
  return (
    <props.PageButtonTrigger page={props.page}>
      <PagerButton type={props.type}>{props.page}</PagerButton>
    </props.PageButtonTrigger>
  )
}

const buttonStyle = cva({
  base: square.raw({
    size: '[32px]',
  }),
  variants: {
    button: {
      true: {
        border: 'medium',
        rounded: 'md',
        bgColor: 'white',
        transition: '[opacity 0.2s]',
        _hover: { opacity: '75' },
      },
      false: { color: 'gray.800' },
    },
    disabled: {
      true: {},
    },
  },
  compoundVariants: [
    {
      button: true,
      disabled: true,
      css: { color: 'gray.400', border: 'light', _hover: { opacity: '[1]' } },
    },
  ],
})

function PagerButton(
  props: PropsWithChildren<{ type: PagerItemProps['type']; disabled?: boolean }>
) {
  const button = ['page', 'prev', 'next'].includes(props.type)
  return <div className={buttonStyle({ button, disabled: props.disabled })}>{props.children}</div>
}

type PagerItemProps =
  // 現在のページ番号
  | { type: 'current'; page: number }
  // クリッカブルなページ番号
  | { type: 'page'; page: number }
  // 省略
  | { type: 'ellipsis' }
  // 前へ (page がないときは disabled)
  | { type: 'prev'; page?: number }
  // 次へ (page がないときは disabled)
  | { type: 'next'; page?: number }

function pagerItems({
  pageSize,
  current,
  total,
  around,
  showPrevNext,
  showLast,
}: Pick<
  Props,
  'pageSize' | 'current' | 'total' | 'around' | 'showPrevNext' | 'showLast'
>): PagerItemProps[] {
  const pageCount = Math.ceil(total / pageSize)
  const start = Math.max(2, current - around) // 1 は常に表示する
  // showLast の場合は最後のページ番号はそっちで書くので -1 する
  const last = showLast ? pageCount - 1 : pageCount
  const end = Math.min(last, current + around)

  const items: PagerItemProps[] = []
  if (showPrevNext) {
    items.push({ type: 'prev', page: current === 1 ? undefined : current - 1 })
  }
  items.push({ type: current === 1 ? 'current' : 'page', page: 1 })
  if (start > 2) {
    items.push({ type: 'ellipsis' })
  }
  for (let i = start; i <= end; i++) {
    items.push({ type: i === current ? 'current' : 'page', page: i })
  }
  if (end < last) {
    // このへん怪しい
    items.push({ type: 'ellipsis' })
  }
  if (showLast) {
    items.push({ type: 'page', page: pageCount })
  }
  if (showPrevNext) {
    items.push({ type: 'next', page: current === pageCount ? undefined : current + 1 })
  }

  return items
}

if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest

  describe('pagerItems', () => {
    test('1ページの場合', () => {
      expect(
        pagerItems({ pageSize: 20, current: 1, total: 1, around: 2, showLast: false })
      ).toEqual([{ type: 'current', page: 1 }])
    })

    test('5ページのうち1ページ目を表示', () => {
      expect(
        pagerItems({ pageSize: 20, current: 1, total: 100, around: 2, showLast: false })
      ).toEqual([
        { type: 'current', page: 1 },
        { type: 'page', page: 2 },
        { type: 'page', page: 3 },
        { type: 'ellipsis' },
      ])
    })

    test('5ページで2ページ目 ellipsis あり', () => {
      expect(
        pagerItems({ pageSize: 20, current: 2, total: 100, around: 2, showLast: false })
      ).toEqual([
        { type: 'page', page: 1 },
        { type: 'current', page: 2 },
        { type: 'page', page: 3 },
        { type: 'page', page: 4 },
        { type: 'ellipsis' },
      ])
    })
  })
}

export function pageToIndexRange(page: number, totalCount: number, pageSize: number) {
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalCount)
  return start > totalCount ? undefined : [start, end]
}
if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest

  describe('pageToIndexRange', () => {
    test.each([
      [1, 21, [1, 20]],
      [2, 21, [21, 21]],
      [3, 21, undefined],
    ])('pageToIndexRange($page, $totalCount)', (page, totalCount, expected) => {
      expect(pageToIndexRange(page, totalCount, 20)).toEqual(expected)
    })
  })
}
